import Card from '@mui/material/Card'
import Grid2 from '@mui/material/Grid2'
import Typography from '@mui/material/Typography'
import { compose, equals, indexBy, insert, isEmpty, map, omit, pathOr, prop } from 'ramda'
import React, { Component } from 'react'
import { v1 as uuid } from 'uuid'

import Accordion from '@/app/component/atom/accordion'
import DeleteDialog from '@/app/component/atom/delete-dialog'
import TablePagination from '@/app/component/atom/table-pagination'
import { duplicateReminderMessage } from '@/app/module/campaigns/models/reminder/message'
import {
  isReminderCampaign,
  reminderMessagesToParts,
  reminderPartsToMessages,
} from '@/app/module/campaigns/models/reminder/transform'
import type {
  CampaignTemplateType,
  ConditionActions,
  Language,
  Message,
  MessagesState,
  MultimessageItem,
  Part,
  ReminderMessage,
} from '@/app/module/campaigns/types'
import { countActionsInCallResult } from '@/app/module/campaigns/utils/actions/list'
import { fromMessage, toMessage } from '@/app/module/campaigns/utils/part-content'
import { duplicateMessage } from '@/app/module/campaigns/utils/part-content/duplicate'
import type { FileUploadType } from '@/app/types'

import CallResults from './components/call-results'
import { findJumpsSource } from './content'
import { contentTypeList, pageSizeValue, QUOTA_ACTIONS_DISABLED_REASON } from './definitions'
import { MessageActions } from './message-actions'
import { NoMessage } from './no-message'
import PartItem from './part-item'
import { getDefaultMessageType } from './utils/default-message'
import { getEmptyMessage } from './utils/empty-message'
import {
  createWelcomeMessage,
  getWelcomeContent,
  hasWelcomeContent,
  updateWelcomeContent,
  WelcomeChange,
} from './utils/welcome'
import WelcomeMessage from './welcome-message'

type ContentItem = MultimessageItem & { id?: number }
type ContentMessage = Message | ReminderMessage
type ContentState = { messages: Record<string, ContentMessage>; ordered: string[] }
type PartProps = React.ComponentProps<typeof PartItem>
type Props = Pick<
  PartProps,
  | 'countriesTimezones'
  | 'countryCode'
  | 'files'
  | 'groups'
  | 'loading'
  | 'newItems'
  | 'orgId'
  | 'published'
  | 'segments'
  | 'timezone'
  | 'timezones'
  | 'token'
  | 'voiceList'
  | 'createNotification'
  | 'onMicAccessError'
  | 'setLoading'
> & {
  activePart?: string | null
  id?: string
  item: ContentItem
  messages: Pick<MessagesState, 'apiPersonalizationList' | 'personalizationList' | 'senderIds' | 'whatsappSenderIds'>
  template?: string
  type: CampaignTemplateType
  addNewItem: (item: { id: string }) => void
  getFile: (args: { msgId: string; file: string }) => ReturnType<PartProps['getFile']>
  onChange: (item: ContentItem) => void
  saveFile: (args: { msgId: string; item: FileUploadType }) => ReturnType<PartProps['saveFile']>
  setActivePart: (id?: string) => void
}

type LegacyContentType = Exclude<ContentItem['type'], CampaignTemplateType.Reminder>

type CampaignContentState =
  | { type: CampaignTemplateType.Reminder; messages: Record<string, ReminderMessage> }
  | { type: LegacyContentType; messages: Record<string, Message> }

type State = CampaignContentState & {
  ordered: string[]
  changeWarn: boolean
  conditions?: number
  deleteConfirm: boolean
  deleteText?: React.ReactNode
  page: number
  pageSize: number
  toDelete?: string
  voicePlayingId?: string
  welcome?: WelcomeChange & { id: string }
}

type MessageInfo = { id: string; rect: DOMRect }

const generateDeleteText = (foundParts: string[]) => (
  <React.Fragment>
    <div>
      This question cannot be deleted because there are still <strong>“Jump to Question”</strong> actions with this
      question as target. Please change or delete the actions in these questions to be able to delete this question:
    </div>
    <div
      style={{
        marginTop: '30px',
      }}
    >
      {foundParts.map((part, i) => (
        <Typography
          key={i}
          style={{
            fontWeight: 600,
          }}
        >
          {part}
        </Typography>
      ))}
    </div>
  </React.Fragment>
)

class CampaignMultiContent extends Component<Props, State> {
  firstMessageRef = React.createRef<HTMLDivElement>()

  state: State = {
    deleteConfirm: false,
    changeWarn: false,
    page: 0,
    pageSize: 10,
    welcome: createWelcomeMessage(this.props.item),
    ...getInitialContent(this.props.item),
    ordered: getOrdered(this.props.item),
  }

  defaultArray: Language[] = []

  defaultObject = {}

  // to prevent rerunning guard function on every update
  CONTENT_CACHE: Record<string, unknown> = {}

  unmounting = false

  scrollElement: HTMLElement | null = null

  scrolling = false

  scrollTimeout?: ReturnType<typeof setTimeout>

  static validate() {
    return true
  }

  componentDidMount() {
    const { activePart, item, type, template, timezone } = this.props
    if (isEmpty(pathOr({}, ['variables', 'parts'], item))) {
      const defaultMessageType = getDefaultMessageType(type, template)

      if (defaultMessageType) {
        this.addMessage(0, getEmptyMessage(type, defaultMessageType, timezone, 0))
      }
    }

    if (activePart) {
      this.scrollToMessage(activePart)
    }

    this.scrollElement = document.getElementById('scrollable-area')
    this.scrollElement?.addEventListener('scroll', this.handleContainerScroll)
  }

  componentDidUpdate(prevProps: Props) {
    const { activePart, item, setActivePart } = this.props
    const { page, pageSize } = this.state

    // for EDIT cases where the item's request gets received after mount
    if (prevProps.item.id !== item.id) {
      this.setState({
        ...getInitialContent(item),
        ordered: getOrdered(item),
        welcome: createWelcomeMessage(item),
      })
    } else {
      // for reorder messages case
      const previousOrdered = getOrdered(prevProps.item)
      const currentOrdered = getOrdered(item)

      if (!equals(previousOrdered, currentOrdered)) {
        let newPage = page
        // if the active part is not in current page (moved to another page), set the page to its new page
        if (activePart) {
          const index = currentOrdered.indexOf(activePart)
          newPage = Math.floor(index / pageSize)
        }

        this.setState({
          ...getInitialContent(item),
          ordered: currentOrdered,
          page: newPage,
        })
      }
    }

    if (activePart) {
      if (prevProps.activePart !== activePart) {
        if (!this.scrolling) {
          this.scrollToMessage(activePart)
        }
      } else {
        const pageMessages = this.getPageMessage()
        if (!pageMessages.includes(activePart)) {
          setActivePart(pageMessages[0])
        }
      }
    }
  }

  componentWillUnmount() {
    this.unmounting = true
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }
    if (this.scrollElement) {
      this.scrollElement.removeEventListener('scroll', this.handleContainerScroll)
    }
  }

  handleContainerScroll = () => {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }
    this.scrolling = true

    this.scrollTimeout = setTimeout(() => {
      this.scrolling = false
    }, 50)

    const scrollElement = this.scrollElement
    if (!scrollElement) {
      return
    }
    const messages = this.getPageMessage()
    const messageInfos = messages
      .map((id) => {
        const elem = document.getElementById(`message-${id}`)
        if (!elem) {
          return null
        }
        const rect = elem.getBoundingClientRect()
        return {
          id,
          rect,
        }
      })
      .filter((info): info is MessageInfo => info !== null)
    if (!messageInfos.length) {
      return
    }
    const containerRect = scrollElement.getBoundingClientRect()
    const closest = messageInfos.reduce((lastClosest, info, index) => {
      // if scrolled to the bottom, set the last item as active
      const containerScrollHeight = scrollElement.scrollHeight
      const containerScrollTop = scrollElement.scrollTop
      const snapThreshold = 1.05
      const remainingScroll = containerScrollHeight - containerScrollTop
      const remainingThreshold = remainingScroll / containerRect.height
      if (index === messageInfos.length - 1 && remainingThreshold <= snapThreshold) {
        return info
      }

      const lastTop = lastClosest.rect.y
      const lastBottom = lastClosest.rect.y + lastClosest.rect.height

      const containerTop = containerRect.y
      const containerBottom = containerRect.y + containerRect.height

      // check if the last item still visible
      const isTopEdgeVisible = lastTop >= containerTop && lastTop <= containerBottom
      const isBottomVisible = lastBottom >= containerTop && lastBottom <= containerBottom
      const isWrappedOver = lastTop <= containerTop && lastBottom >= containerBottom // top is above viewport and bottom is below viewport

      const isVisible = isTopEdgeVisible || isBottomVisible || isWrappedOver

      if (isVisible) {
        return lastClosest
      }
      return info
    })

    this.props.setActivePart(closest.id)
  }

  scrollToMessage = (id: string) => {
    if (this.scrolling) {
      return
    }

    const index = this.state.ordered.indexOf(id)
    const page = Math.floor(index / this.state.pageSize)
    if (index > -1) {
      if (page !== this.state.page) {
        this.setState(
          {
            page,
          },
          () => {
            document.getElementById(`message-${id}`)?.scrollIntoView()
            this.focusInput(id)
          },
        )
      } else {
        document.getElementById(`message-${id}`)?.scrollIntoView()
        this.focusInput(id)
      }
    }
  }

  focusInput = (id: string) => {
    setTimeout(() => {
      document.querySelector<HTMLElement>(`#message-${id} #send-message-text`)?.focus({
        preventScroll: true,
      })
    }, 0)
  }

  changeHandler = (value: Partial<ContentMessage> & { id: string }, changed = true) => {
    this.update(({ messages, ordered }) => ({
      ordered,
      messages: {
        ...messages,
        [value.id]: {
          ...messages[value.id],
          ...value,
          changed,
        } as ContentMessage,
      },
    }))
  }

  welcomeChangeHandler = (welcomeId: string, value: WelcomeChange = {}) => {
    if (welcomeId !== this.state.welcome?.id) {
      return
    }

    const { item, onChange } = this.props
    onChange(updateWelcomeContent(item, value))
    this.setState(({ welcome }) => ({
      welcome: welcome ? { ...value, id: welcome.id } : undefined,
    }))
  }

  callResultActionsChangeHandler = ({ value = [] }: { value?: ConditionActions[] }) => {
    const { item, onChange } = this.props
    if (item.type !== 'ivr') return

    onChange({
      ...item,
      variables: {
        ...item.variables,
        callResultActions: value,
      },
    })
  }

  getFileHandler = (msgId: string, file: string) => {
    const { getFile } = this.props

    return getFile({
      msgId,
      file,
    })
  }

  saveFileHandler = (msgId: string, { file, filename, filter, type }: FileUploadType) => {
    const { saveFile } = this.props

    return saveFile({
      msgId,
      item: {
        file,
        filename,
        filter,
        type,
      },
    })
  }

  update(fn: (state: State) => Partial<ContentState>) {
    const { item, onChange } = this.props
    const { messages = {}, ordered = [] } = fn(this.state)

    if (this.unmounting) {
      onChange({
        ...item,
        variables: {
          ...item.variables,
          parts: isReminderCampaign(item)
            ? reminderMessagesToParts(messages as Record<string, ReminderMessage>, ordered)
            : ordered.map((id) => fromMessage(messages[id])),
        },
      } as ContentItem)
    } else {
      this.setState(
        {
          messages,
          ordered,
        },
        () => {
          onChange({
            ...item,
            variables: {
              ...item.variables,
              parts: isReminderCampaign(item)
                ? reminderMessagesToParts(messages as Record<string, ReminderMessage>, ordered)
                : ordered.map((id) => fromMessage(messages[id])),
            },
          } as ContentItem)
        },
      )
    }
  }

  addMessage = (index: number | null | undefined, message: ContentMessage) => {
    const ensuredIndex = index ?? 0
    this.props.addNewItem({
      id: message.id,
    })
    this.update(({ messages, ordered }) => ({
      messages: {
        ...messages,
        [message.id]: message,
      },
      ordered: [...ordered.slice(0, ensuredIndex), message.id, ...ordered.slice(ensuredIndex)],
    }))
  }

  checkForJumps = ({ id }: { id: string }) => {
    if (this.props.item.type === 'drip') {
      this.removeMessage(id)
    } else {
      const parts = pathOr([], ['item', 'variables', 'parts'], this.props)
      const foundParts = findJumpsSource(id, parts)
      if (foundParts.length) {
        this.setState({
          deleteConfirm: true,
          toDelete: id,
          deleteText: generateDeleteText(foundParts),
        })
      } else {
        this.removeMessage(id)
      }
    }
  }

  removeMessage = (id: string) => {
    this.update(({ messages, ordered }) => {
      return {
        messages: omit([id], messages),
        ordered: ordered.filter((msgId) => msgId !== id),
      }
    })
  }

  duplicateMessage = (id: string) => {
    this.update(({ messages, ordered }) => {
      const { item, addNewItem } = this.props

      const index = ordered.indexOf(id)
      const newId = uuid()
      addNewItem({
        id: newId,
      })
      const message = messages[id]
      const newOrdered = insert(index + 1, newId, ordered)

      if (isReminderCampaign(item)) {
        return {
          messages: {
            ...messages,
            [newId]: duplicateReminderMessage(message as ReminderMessage, newId),
          },
          ordered: newOrdered,
        }
      }

      const duplicatedMessage = duplicateMessage(this.state.type, message, newId)
      return {
        messages: {
          ...messages,
          [newId]: duplicatedMessage,
        },
        ordered: newOrdered,
      }
    })
  }

  renderIVRCallResultActions = (children: NonNullable<React.ReactNode>, actions: ConditionActions[]) => {
    return (
      <Accordion
        display={countActionsInCallResult(actions) > 0}
        placeholder="Click here to specify what actions we should take if the contact does/ doesn't answer the call"
        title="Call Result Actions"
      >
        {children}
      </Accordion>
    )
  }

  renderWelcome = () => {
    if (this.state.page) {
      return null
    }

    const { item, messages } = this.props
    const { welcome } = this.state
    const content = hasWelcomeContent(getWelcomeContent(item))

    if (!content || !welcome) {
      // welcome is deprecated
      return null
    }

    return (
      <WelcomeMessage
        {...this.props}
        campaignType={this.state.type}
        content={content}
        id={welcome.id}
        key={welcome.id}
        personalizationList={messages.personalizationList || this.defaultObject}
        defaultLanguage={pathOr('', ['uiStore', 'defaultLanguage'], this.props.item)}
        languages={pathOr(this.defaultArray, ['uiStore', 'languages'], this.props.item)}
        voicePlayingId={this.state.voicePlayingId}
        welcome={welcome}
        getFile={this.getFileHandler}
        onChange={(value) => this.welcomeChangeHandler(welcome.id, value)}
        onMicAccessError={this.props.onMicAccessError}
        saveFile={this.saveFileHandler}
        setPlayingId={this.setPlayingHandler}
      />
    )
  }

  closeHandler = (msgId: string) => this.checkForJumps({ id: msgId })

  scrollToFirstMessage = () => {
    const firstMessage = this.firstMessageRef.current
    if (firstMessage) {
      window.scrollTo(0, firstMessage.offsetTop)
    }
  }

  createMessageHandler = (index: number, row: number, message: ContentMessage) => {
    this.addMessage(index, message)
    if (row === this.state.pageSize - 1) {
      this.setState(
        {
          page: this.state.page + 1,
        },
        this.scrollToFirstMessage,
      )
    }
  }

  duplicateMessageHandler = (id: string, row: number) => {
    if (row === this.state.pageSize - 1) {
      this.setState(
        {
          page: this.state.page + 1,
        },
        this.scrollToFirstMessage,
      )
    }
    this.duplicateMessage(id)
  }

  saveConditionsHandler = (item: Message) => {
    this.changeHandler(item)
    this.setState({ conditions: undefined })
  }

  setPlayingHandler = (val?: string) => {
    this.setState({
      voicePlayingId: val,
    })
  }

  pageMessages: string[] = []

  getPageMessage = () => {
    const { ordered, page, pageSize } = this.state

    const newPageMessages = ordered.slice(page * pageSize, page * pageSize + pageSize)
    if (newPageMessages.toString() !== this.pageMessages.toString()) {
      this.pageMessages = newPageMessages
    }

    return this.pageMessages
  }

  getSupportedMessageTypes = () => {
    const hasQuotas = Boolean(this.props.item.quotas?.length)
    return contentTypeList[this.state.type].map((messageType) =>
      messageType.value === 'quota-q'
        ? {
            ...messageType,
            disabled: !hasQuotas,
            disabledReason: !hasQuotas ? QUOTA_ACTIONS_DISABLED_REASON : undefined,
          }
        : messageType,
    )
  }

  setConditions = (conditions?: number) => this.setState({ conditions })

  render() {
    const { item, timezones, timezone, countriesTimezones, countryCode, setLoading, published } = this.props
    const callResultActions = item.type === 'ivr' ? (item.variables.callResultActions ?? []) : []
    const isPagingEnabled = this.state.ordered.length > 5 - 1
    const parts = this.getPageMessage()
    const showTopAddPart = this.props.item.type === 'cati' || this.props.item.type === 'ivr'
    const supportedMessageTypes = this.getSupportedMessageTypes()

    return (
      <div>
        <DeleteDialog
          text={this.state.deleteText}
          isOpen={this.state.deleteConfirm}
          hideDeleteButton={true}
          cancelButtonText="Close"
          onClose={() =>
            this.setState({
              deleteConfirm: false,
              toDelete: undefined,
            })
          }
          onConfirm={() => {
            const { toDelete } = this.state
            if (toDelete !== undefined) {
              this.removeMessage(toDelete)
            }
          }}
        />

        {this.renderWelcome()}
        {item.type === 'ivr' && this.state.page === 0 && (
          <div
            style={{
              marginBottom: '18px',
            }}
          >
            {this.renderIVRCallResultActions(
              <CallResults
                actions={callResultActions}
                campaignType={item.type}
                countriesTimezones={countriesTimezones}
                countryCode={countryCode}
                defaultLanguage={pathOr('', ['uiStore', 'defaultLanguage'], this.props.item)}
                files={this.props.files}
                languages={pathOr(this.defaultArray, ['uiStore', 'languages'], this.props.item)}
                orgId={this.props.orgId}
                personalizationList={this.props.messages.personalizationList}
                senderIds={this.props.messages.senderIds}
                token={this.props.token}
                timezone={timezone}
                timezones={timezones}
                voiceList={this.props.voiceList}
                voicePlayingId={this.state.voicePlayingId}
                whatsappSenderIds={this.props.messages.whatsappSenderIds}
                changeHandler={this.callResultActionsChangeHandler}
                getFile={this.getFileHandler}
                onMicAccessError={this.props.onMicAccessError}
                saveFile={this.saveFileHandler}
                setPlayingId={this.setPlayingHandler}
              />,
              callResultActions,
            )}
          </div>
        )}

        {parts.length === 0 && (
          <NoMessage
            campaignType={this.state.type}
            messageIds={parts}
            supportedMessageTypes={supportedMessageTypes}
            timezone={timezone}
            onCreateMessage={(message) => this.createMessageHandler(0, 0, message)}
          />
        )}

        {showTopAddPart && this.state.page === 0 && parts.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '18px',
            }}
          >
            <MessageActions
              index={-1}
              campaignType={this.state.type}
              total={this.state.ordered.length}
              published={published}
              supportedMessageTypes={supportedMessageTypes}
              messageIds={parts}
              hideDuplicate
              timezone={timezone}
              onMessageCreate={(message) => this.createMessageHandler(0, 0, message)}
              onMessageDuplicate={() => {}}
            />
          </div>
        )}

        {parts.map((id, row) => {
          const index = this.state.page * this.state.pageSize + row
          const messageProps = getMessageProps(this.state, id)
          const { hasQuestion } = messageProps.message

          return (
            <PartItem
              {...messageProps}
              apiPersonalizationList={this.props.messages.apiPersonalizationList}
              messageIds={parts}
              conditions={this.state.conditions}
              countriesTimezones={countriesTimezones}
              countryCode={countryCode}
              defaultLanguage={pathOr('', ['uiStore', 'defaultLanguage'], this.props.item)}
              files={this.props.files}
              groups={this.props.groups}
              hasQuestion={hasQuestion}
              id={id}
              index={index}
              key={id}
              languages={pathOr(this.defaultArray, ['uiStore', 'languages'], this.props.item)}
              loading={this.props.loading}
              messageRef={row === 0 ? this.firstMessageRef : null}
              newItems={this.props.newItems}
              orgId={this.props.orgId}
              page={this.state.page}
              pageSize={this.state.pageSize}
              messages={this.state.messages}
              ordered={this.state.ordered}
              personalizationList={this.props.messages.personalizationList}
              published={published}
              row={row}
              segments={this.props.segments}
              senderIds={this.props.messages.senderIds}
              supportedMessageTypes={supportedMessageTypes}
              timezone={timezone}
              timezones={timezones}
              token={this.props.token}
              total={this.state.ordered.length}
              voiceList={this.props.voiceList}
              voicePlayingId={this.state.voicePlayingId}
              whatsappSenderIds={this.props.messages.whatsappSenderIds}
              createNotification={this.props.createNotification}
              getFile={this.getFileHandler}
              onChange={this.changeHandler}
              onClose={this.closeHandler}
              onMessageCreate={this.createMessageHandler}
              onMessageDuplicate={this.duplicateMessageHandler}
              onMicAccessError={this.props.onMicAccessError}
              onSaveConditions={this.saveConditionsHandler}
              saveFile={this.saveFileHandler}
              setConditions={this.setConditions}
              setLoading={setLoading}
              setPlayingId={this.setPlayingHandler}
            />
          )
        })}
        {isPagingEnabled && (
          <Card style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
            <Grid2 container spacing={8}>
              <Grid2 size={{ xs: 12 }}>
                <TablePagination
                  namespace="Messages"
                  page={this.state.page + 1}
                  size={this.state.pageSize}
                  total={this.state.ordered.length}
                  pageSizeValues={pageSizeValue}
                  onPageSizeChange={(value) => {
                    this.setState({
                      pageSize: value,
                      page: 0,
                    })
                  }}
                  onPageChange={(page) => {
                    this.setState({
                      page: page - 1,
                    })
                  }}
                />
              </Grid2>
            </Grid2>
          </Card>
        )}
      </div>
    )
  }
}

const getMessageProps = (state: CampaignContentState, id: string) => {
  // It looks weird but the compiler needs this because of the distinction between reminder and other campaign types
  if (state.type === 'reminder') {
    return { campaignType: state.type, message: state.messages[id] }
  }
  return { campaignType: state.type, message: state.messages[id] }
}

const getInitialContent = (item: ContentItem): CampaignContentState =>
  isReminderCampaign(item)
    ? { type: item.type, messages: reminderPartsToMessages(pathOr([], ['variables', 'parts'], item)) }
    : {
        type: item.type,
        messages: compose(
          indexBy<Message>(prop('id')),
          map(toMessage),
          pathOr<Part[]>([], ['variables', 'parts']),
        )(item),
      }

const getOrdered = (item: ContentItem): string[] => compose(map(prop('id')), pathOr([], ['variables', 'parts']))(item)

export default CampaignMultiContent
